// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Overview detector-visual panel.
//
// Renders single-flight detector visuals on the Overview page. Four detectors
// ship today:
//   - `cht`: stacks two Trends CHT visuals for the selected flight (there is no
//     separate CHT distribution tile on Overview), reusing the exact Trends
//     renderers (single source; Trends delegates to the same modules):
//       1. CHT Heat Profile  (cumulative time-in-range bars)   — cht-heat-profile.js
//       2. CHT Distribution Lanes (where each cylinder runs)    — cht-distribution-lanes.js
//   - `sensors`: a per-channel probe-trust swimlane for the flight — sensor-trust-
//     timeline.js. Unlike CHT (recomputed from points), this reads the already-
//     computed `summary.sensors.details` carried on the analysis bundle.
//   - `oilSystem`: the flight's oil picture, three stacked sub-views from
//     oil-pressure-graph.js (built from oilSystemTrendSummary(points)):
//       1. the Trends "Oil Pressure vs Oil Temperature" envelope, then
//       2. + 3. CHT-style single-channel ridgeline distributions for oil
//          temperature and oil pressure (renderValueDistribution — reuses the
//          shared trend-distribution-* grammar with an oil-tuned domain/unit).
//     Trends delegates to this module's renderOilEnvelopeSingle too (single
//     source, same as the CHT modules); renderValueDistribution is new and used
//     only here.
//   - `valve` (EGT Rhythm): each cylinder's real EGT movement from its own
//     steady-window baseline, with cylinder drill-down — egt-rhythm-signature.js.
//     Trends draws a stylized sine from scalars; this recomputes actual display
//     series via egtRhythmWaveforms() in analysis/valve.js, with the stat scalars
//     read from summary.valve.details so the picture matches the tile.
//
// Data: a single flight is one trend row. Overview has no persisted trend row,
// but it has the flight's normalized points (getFlightData) and the active
// engine scope (currentEngineDescriptors); chtHeatBandSummary / chtDistribution-
// Summary / oilSystemTrendSummary produce the per-cylinder / per-flight shapes
// Trends consumes. The result is memoized on (flight id + engine-scope
// signature) so repeated health-summary renders (tile taps, sensitivity nudges)
// don't recompute on the main thread.
//
// Selection-driven: the panel shows only when a supported tile is selected, and
// while it is shown the body carries `overview-detector-active` so CSS hides the
// Operating Context cards (pulling the graphs up into view). Unsupported
// detectors fall back to a hidden panel.

import { createChtHeatProfile } from "./cht-heat-profile.js";
import { createChtDistributionLanes } from "./cht-distribution-lanes.js";
import { createSensorTrustTimeline } from "./sensor-trust-timeline.js";
import { createOilPressureGraph } from "./oil-pressure-graph.js";
import { createEgtRhythmSignature } from "./egt-rhythm-signature.js";
import { createEgtDistributionLanes } from "./egt-distribution-lanes.js";
import { createEgtSpreadTimeline } from "./egt-spread-timeline.js";
import { createCoolingRateGraph } from "./cooling-rate-graph.js";
import { createCoolingMarginGraph } from "./cooling-margin-graph.js";
import { createChtEgtPairingGraph } from "./cht-egt-pairing-graph.js";
import { createElectricalBusGraph } from "./electrical-bus-graph.js";
import { chtHeatBandSummary, chtDistributionSummary, oilSystemTrendSummary, egtDistributionSummary, egtSpreadTimelineSummary } from "../trends/trend-row-builder.js";
import { engineScopedChannelLabel, engineInfoForSourceNumber, projectFlightDataForEngine } from "../core/engines.js";
import { cylinderNumberFromKey } from "../core/schema.js";
import { analyzeSensorValidity } from "../analysis/sensors.js";
import { analyzeExhaustValvePattern, egtRhythmWaveforms } from "../analysis/valve.js";
import { coolingRateTraceForKey, analyzeChtCoolingRate, coolingMarginTraceForKey, analyzeCoolingEfficiency } from "../analysis/cooling.js";
import {
  analyzeCombustionSignatures,
  chtEgtPairingEvaluationState,
  chtEgtPairingTraceForKey,
  chtEgtPairingRoster,
} from "../analysis/combustion.js";
import { analyzeElectrical, electricalBusTrace } from "../analysis/electrical.js";

const SUPPORTED_KEYS = new Set(["cht", "sensors", "oilSystem", "valve", "egt", "coolingRate", "cooling", "combustion", "electrical", "charging"]);
const DETECTOR_ACTIVE_CLASS = "overview-detector-active";

function formatMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0 min";
  if (minutes < 1) return "<1 min";
  const rounded = Math.round(minutes);
  if (rounded < 60) return `${rounded} min`;
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

export function createOverviewDetectorVisuals({
  escapeHtml,
  getFlightData,
  currentEngineDescriptors,
  getSelectedHealthKey,
  getSelectedFlight,
  getSelectedCylinderNumber,
  getSelectedEngineScope,
  getCoolingRateThresholdFpm,
  onChtEgtPairingCardSelect,
}) {
  const chtHeatProfile = createChtHeatProfile({ escapeHtml });
  const chtDistributionLanes = createChtDistributionLanes({ escapeHtml });
  const sensorTrustTimeline = createSensorTrustTimeline({ escapeHtml });
  const oilPressureGraph = createOilPressureGraph({ escapeHtml });
  const egtRhythmSignature = createEgtRhythmSignature({ escapeHtml });
  const egtDistributionLanes = createEgtDistributionLanes({ escapeHtml });
  const egtSpreadTimeline = createEgtSpreadTimeline({ escapeHtml });
  const coolingRateGraph = createCoolingRateGraph({ escapeHtml });
  const coolingMarginGraph = createCoolingMarginGraph({ escapeHtml });
  const chtEgtPairingGraph = createChtEgtPairingGraph({ escapeHtml });
  const electricalBusGraph = createElectricalBusGraph({ escapeHtml });
  // Recently-rendered detector panels keyed by their full render signature
  // (flight id + engine scope + cylinder filter [+ detector-specific parts]).
  // A small LRU instead of a single slot: cycling score tiles or toggling
  // cylinder focus returns to recently-shown panels, and the signature fully
  // determines the HTML (sensitivity does not feed these panels), so serving a
  // cached entry is always byte-correct. Cleared wholesale on flight change.
  const PANEL_HTML_CACHE_MAX = 8;
  const panelHtmlCache = new Map();

  function cachedPanelHtml(signature) {
    const html = panelHtmlCache.get(signature);
    // delete+set recency ordering, same idiom as the parsed-flight caches.
    panelHtmlCache.delete(signature);
    panelHtmlCache.set(signature, html);
    return html;
  }

  function storePanelHtml(signature, html) {
    panelHtmlCache.delete(signature);
    panelHtmlCache.set(signature, html);
    if (panelHtmlCache.size > PANEL_HTML_CACHE_MAX) {
      panelHtmlCache.delete(panelHtmlCache.keys().next().value);
    }
    return html;
  }

  function isOverviewActive() {
    return document.querySelector('.tab[data-panel="overview"]')?.classList.contains("is-active") ?? true;
  }

  function scopeSignature(engineDescriptors) {
    return (engineDescriptors ?? []).map((descriptor) => [
      descriptor?.id ?? "",
      descriptor?.side ?? "",
      // Include the label scheme: panels now render engine.label/shortLabel, so a
      // Left/Right <-> Front/Rear relabel (same id/side) must bust the cache.
      descriptor?.label ?? "",
      descriptor?.shortLabel ?? "",
      (descriptor?.cylinders ?? []).map((cyl) => `${cyl.sourceNumber}:${cyl.number}:${cyl.egtKey}:${cyl.chtKey}`).join("|"),
    ].join(":")).join(",");
  }

  function currentEngineScope() {
    return String(getSelectedEngineScope?.() ?? "aircraft");
  }

  function activeEngineDescriptors(engineDescriptors = currentEngineDescriptors?.() ?? []) {
    const descriptors = Array.isArray(engineDescriptors) ? engineDescriptors : [];
    const scope = currentEngineScope();
    if (scope === "left" || scope === "right") {
      const scoped = descriptors.filter((engine) => engine?.id === scope || engine?.side === scope);
      return scoped.length ? scoped : descriptors;
    }
    return descriptors;
  }

  function activeEngineId(engineDescriptors = currentEngineDescriptors?.() ?? []) {
    const descriptors = activeEngineDescriptors(engineDescriptors);
    const scope = currentEngineScope();
    if ((scope === "left" || scope === "right") && descriptors.length === 1) return descriptors[0]?.id ?? scope;
    return "all";
  }

  function activeSourceNumbers(engineDescriptors = currentEngineDescriptors?.() ?? []) {
    const descriptors = activeEngineDescriptors(engineDescriptors);
    const scope = currentEngineScope();
    if (scope !== "left" && scope !== "right") return null;
    const numbers = descriptors
      .flatMap((engine) => engine?.cylinders ?? [])
      .map((cylinder) => Number(cylinder?.sourceNumber))
      .filter(Number.isFinite);
    return numbers.length ? new Set(numbers) : null;
  }

  function activeFlightData(points, engineDescriptors = currentEngineDescriptors?.() ?? []) {
    const descriptors = activeEngineDescriptors(engineDescriptors);
    const scope = currentEngineScope();
    if ((scope === "left" || scope === "right") && descriptors.length === 1) {
      return projectFlightDataForEngine(points, descriptors[0]);
    }
    return points;
  }

  function sourceNumberForItem(item) {
    return Number(item?.sourceCylinder ?? item?.cylinder ?? cylinderNumberFromKey(item?.key));
  }

  function matchesActiveEngine(item, sourceNumbers) {
    if (!sourceNumbers) return true;
    const source = sourceNumberForItem(item);
    return Number.isFinite(source) && sourceNumbers.has(source);
  }

  function scopedCylinderLabel(sourceNumber, engineDescriptors = currentEngineDescriptors?.() ?? [], fallback = null) {
    const source = Number(sourceNumber);
    if (!Number.isFinite(source)) return fallback ?? "Cyl ?";
    const { engine, cylinder } = engineInfoForSourceNumber(source, engineDescriptors);
    if (engine && cylinder && (engineDescriptors?.length ?? 0) > 1) {
      return `${engine.shortLabel ?? engine.label ?? "Engine"} Cyl ${cylinder.number}`;
    }
    return fallback ?? `Cyl ${cylinder?.number ?? source}`;
  }

  function relabelCylinderSummary(item, engineDescriptors = currentEngineDescriptors?.() ?? []) {
    if (!item || typeof item !== "object") return item;
    const source = sourceNumberForItem(item);
    if (!Number.isFinite(source)) return item;
    const label = scopedCylinderLabel(source, engineDescriptors, item.label ?? `Cyl ${source}`);
    const { cylinder } = engineInfoForSourceNumber(source, engineDescriptors);
    return { ...item, sourceCylinder: source, displayCylinder: cylinder?.number ?? source, label };
  }

  function labelForKey(key, engineDescriptors = currentEngineDescriptors?.() ?? []) {
    return engineScopedChannelLabel(key, engineDescriptors) ?? undefined;
  }

  // oilSystemTrendSummary takes a quantile fn (the Trends builder injects one).
  function quantile(values, fraction) {
    const sorted = (values ?? []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    if (!sorted.length) return null;
    const position = Math.max(0, Math.min(sorted.length - 1, (sorted.length - 1) * fraction));
    const lower = Math.floor(position);
    const upper = Math.ceil(position);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
  }

  function subsection(title, blurb, body) {
    return `
      <section class="overview-detector-subsection">
        <header class="overview-detector-subhead">
          <p class="eyebrow">${escapeHtml(title)}</p>
          <p class="overview-detector-subhead-blurb">${escapeHtml(blurb)}</p>
        </header>
        ${body}
      </section>
    `;
  }

  // Extra Heat-exposure tiles unique to Overview (single flight). Peak + spread come
  // from the per-cylinder distribution stats; the over-425 time from the heat bands.
  // All respect the active cylinder focus via the cylinderFilter passed by the caller.
  function heatExtraTiles(heatAgg, distRow, cylinderFilter, engineId = "all") {
    const tiles = [];
    const insights = chtDistributionLanes.aggregateDistributionInsights([distRow], { engineId, cylinderFilter });
    const lanes = insights?.lanes ?? [];

    const peak = lanes.reduce((best, lane) => (Number.isFinite(lane.max) && lane.max > (best?.max ?? -Infinity) ? lane : best), null);
    if (peak) tiles.push({ label: "Peak CHT", value: `${Math.round(peak.max)} F`, sub: peak.label ?? "" });

    const ranked = lanes.filter((lane) => Number.isFinite(lane.p95)).sort((a, b) => b.p95 - a.p95);
    if (ranked.length >= 2) {
      const hot = ranked[0];
      const cool = ranked[ranked.length - 1];
      tiles.push({ label: "Cylinder spread", value: `${Math.round(hot.p95 - cool.p95)} F`, sub: `${hot.label} vs ${cool.label}` });
    } else if (cylinderFilter !== "all") {
      tiles.push({ label: "Cylinder spread", value: "—", sub: "single cylinder" });
    }

    const extreme = (heatAgg ?? [])
      .map((cyl) => ({ label: cyl.label, minutes: cyl.bands.find((band) => band.key === "425+")?.minutes ?? 0 }))
      .sort((a, b) => b.minutes - a.minutes)[0];
    if (extreme) {
      tiles.push(extreme.minutes > 0
        ? { label: "Over 425 F", value: formatMinutes(extreme.minutes), sub: extreme.label }
        : { label: "Over 425 F", value: "0 min", sub: "stayed below 425 F" });
    }
    return tiles;
  }

  function chtPanelHtml() {
    const points = getFlightData?.();
    if (!Array.isArray(points) || !points.length) {
      return wrapPanel(emptyStateHtml("Analyze a flight with CHT data to see its heat profile."));
    }
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const engineId = activeEngineId(engineDescriptors);
    const flight = getSelectedFlight?.();
    // When a cylinder is focused (Cyl N in the scope switch) scope both graphs to
    // just that cylinder; otherwise show every cylinder in the current engine scope.
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const signature = `cht|${flight?.id ?? ""}|${currentEngineScope()}|${scopeSignature(engineDescriptors)}|${cylinderFilter}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const opts = { engineId, cylinderFilter, density: "overview" };
    const heatRow = { chtHeatBandsByCylinder: chtHeatBandSummary(points, { engineDescriptors }) };
    const distRow = { chtDistributionByCylinder: chtDistributionSummary(points, { engineDescriptors }) };
    const matchesFilter = (item) => cylinderFilter === "all" || sourceNumberForItem(item) === Number(cylinderFilter);
    const heatAgg = chtHeatProfile.aggregateHeatBands([heatRow], engineId).filter(matchesFilter);
    const distAgg = chtDistributionLanes.aggregateDistribution([distRow], engineId).filter(matchesFilter);
    const hasHeat = heatAgg.some((item) => item.totalMinutes > 0);
    const hasDist = distAgg.some((item) => item.samples > 0);
    const focusLabel = cylinderFilter === "all" ? null : ((heatAgg[0] || distAgg[0])?.label ?? `Cyl ${cylinderFilter}`);
    const extraTiles = hasHeat ? heatExtraTiles(heatAgg, distRow, cylinderFilter, engineId) : [];

    let body;
    if (!hasHeat && !hasDist) {
      body = emptyStateHtml(focusLabel
        ? `No warm CHT operating time for ${focusLabel} in this flight.`
        : "No warm CHT operating time in this flight to profile.");
    } else {
      const heatSection = hasHeat
        ? subsection(
          "Heat exposure",
          focusLabel
            ? `How much operating time ${focusLabel} spent in each CHT range.`
            : "How much operating time each cylinder spent in each CHT range.",
          `${chtHeatProfile.renderHeatProfileDetail([heatRow], { ...opts, singleFlight: true, extraTiles })}${chtHeatProfile.renderHeatProfile([heatRow], opts)}`,
        )
        : "";
      const distSection = hasDist
        ? subsection(
          "Cylinder distribution",
          focusLabel
            ? `Where ${focusLabel} usually runs, on the shared CHT scale.`
            : "Where each cylinder usually runs, on a shared CHT scale.",
          `${chtDistributionLanes.renderDistributionDetail([distRow], opts)}${chtDistributionLanes.renderDistributionStage([distRow], { ...opts, singleFlight: true })}`,
        )
        : "";
      body = heatSection + distSection;
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // Sensor probe-trust swimlane. Reads the already-computed per-channel detail
  // off the rendered health summary (summary.sensors.details) — no re-analysis.
  // Points are pulled only for the time axis + phase ribbon (a light scan).
  function sensorPanelHtml(summary) {
    const points = getFlightData?.() ?? [];
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const scopedPoints = activeFlightData(points, engineDescriptors);
    const scope = currentEngineScope();
    const details = (scope === "left" || scope === "right")
      ? analyzeSensorValidity(scopedPoints, { labelForKey: (key) => labelForKey(key, engineDescriptors) }).details
      : summary?.sensors?.details;
    if (!Array.isArray(details?.summaries) || !details.summaries.length) {
      return wrapPanel(emptyStateHtml("Analyze a flight with engine sensor data to check probe trust."));
    }
    const flight = getSelectedFlight?.();
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const signature = `sensors|${flight?.id ?? ""}|${scope}|${scopeSignature(engineDescriptors)}|${cylinderFilter}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const model = sensorTrustTimeline.buildModel(details, { points, cylinderFilter });
    const focusLabel = cylinderFilter === "all" ? null : `Cyl ${cylinderFilter}`;

    let body;
    if (!model.channelCount) {
      body = emptyStateHtml(focusLabel
        ? `No sensor channels for ${focusLabel} in this flight.`
        : "No sensor channels to check in this flight.");
    } else if (!model.problemLanes.length) {
      body = subsection(
        "Probe trust",
        focusLabel
          ? `Signal integrity for ${focusLabel} across this flight.`
          : "Signal integrity for every channel across this flight.",
        sensorTrustTimeline.renderCleanState(model),
      );
    } else {
      body = subsection(
        "Probe trust",
        focusLabel
          ? `Where ${focusLabel} probes lost trust during this flight.`
          : "Where each probe lost trust during this flight — marks aligned in time across channels point to a shared fault.",
        `${sensorTrustTimeline.renderTrustDetail(model)}${sensorTrustTimeline.renderTrustTimeline(model)}`,
      );
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // Oil tile: the bundled "Oil Pressure vs Oil Temperature" envelope for this
  // flight — the warm-pressure histogram + oil-temp distribution + envelope band.
  // Reuses the Trends renderer; built from points via oilSystemTrendSummary.
  function oilPanelHtml(summary) {
    const points = getFlightData?.();
    if (!Array.isArray(points) || !points.length) {
      return wrapPanel(emptyStateHtml("Analyze a flight with oil data to see its pressure/temperature envelope."));
    }
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const scope = currentEngineScope();
    const flight = getSelectedFlight?.();
    const signature = `oil|${flight?.id ?? ""}|${scope}|${scopeSignature(engineDescriptors)}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    // Whole-aircraft view on a twin: one oil envelope per engine, stacked, so the
    // left and right engines can be read on their own instead of blended into an
    // averaged band (issue #233). The Trends oil view already splits per engine in
    // "all" mode (trend-charts/oil-pressure.js renderEnvelope); this mirrors it on
    // Overview. Single-engine and per-engine (Left/Right) scopes are unchanged.
    const perEngine = scope === "aircraft" && Array.isArray(engineDescriptors) && engineDescriptors.length > 1;
    const body = perEngine
      ? twinOilPanelBody(points, engineDescriptors, summary, flight)
      : mergedOilPanelBody(points, engineDescriptors, summary, flight);

    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // A single oil picture — one engine's data (a single-engine aircraft, or the
  // Left/Right scope on a twin): the warm-pressure envelope + oil-temp and
  // oil-pressure ridgeline distributions. This is the original Overview oil panel.
  function mergedOilPanelBody(points, engineDescriptors, summary, flight) {
    const scopedPoints = activeFlightData(points, engineDescriptors);
    const oilTrend = oilSystemTrendSummary(scopedPoints, quantile);
    const hasData = Array.isArray(oilTrend?.envelopeSamples) && oilTrend.envelopeSamples.length > 0;
    if (!hasData) {
      return emptyStateHtml("No paired oil pressure + temperature samples in this flight to chart.");
    }
    const row = {
      oilTrend,
      oilSystemScore: summary?.oilSystem?.score,
      dateLabel: flight?.title ?? "This flight",
      title: flight?.title ?? "",
    };
    const samples = oilTrend.envelopeSamples;
    return (
      subsection(
        "Oil pressure vs temperature",
        "How oil pressure settles as the oil warms — the warm-pressure band is the envelope read — for this flight.",
        oilPressureGraph.renderOilEnvelopeSingle([row], { singleFlight: true, idSuffix: "Overview" }),
      ) +
      subsection(
        "Oil temperature distribution",
        "Where the oil temperature spent its time this flight — same ridgeline grammar as the CHT distribution.",
        oilPressureGraph.renderValueDistribution(samples.map((sample) => sample.temperature), {
          unit: "F",
          label: "Oil temperature",
          highLabel: "warm end",
          gradientId: "trendOilTempDistribution",
          gradientStops: [[0, "#5aa7ff"], [60, "#62cfc1"], [100, "#ffb347"]],
        }),
      ) +
      subsection(
        "Oil pressure distribution",
        "Where the oil pressure spent its time this flight.",
        oilPressureGraph.renderValueDistribution(samples.map((sample) => sample.pressure), {
          unit: "psi",
          label: "Oil pressure",
          highLabel: "high end",
          gradientId: "trendOilPressureDistribution",
          gradientStops: [[0, "#7c8cf0"], [100, "#43c6ac"]],
        }),
      )
    );
  }

  // Twin, whole-aircraft view: one warm-pressure envelope per engine, stacked, so
  // left and right can be compared directly rather than averaged into one band
  // (issue #233). Each envelope recomputes from that engine's own oil channels
  // (projectFlightDataForEngine), and gets its own paint-server id namespace so
  // gradients/markers can't collide between the two coexisting SVGs.
  function twinOilPanelBody(points, engineDescriptors, summary, flight) {
    const sections = engineDescriptors
      .map((engine) => {
        const enginePoints = projectFlightDataForEngine(points, engine);
        const oilTrend = oilSystemTrendSummary(enginePoints, quantile);
        const hasData = Array.isArray(oilTrend?.envelopeSamples) && oilTrend.envelopeSamples.length > 0;
        if (!hasData) return "";
        const row = {
          oilTrend,
          oilSystemScore: summary?.oilSystem?.score,
          dateLabel: flight?.title ?? "This flight",
          title: flight?.title ?? "",
        };
        const idSuffix = `Overview${String(engine.id ?? "").replace(/[^A-Za-z0-9]/g, "")}`;
        const label = engine.label ?? `${engine.shortLabel ?? "Engine"} Engine`;
        return `
          <section class="overview-oil-engine-panel">
            <p class="overview-oil-engine-heading">${escapeHtml(label)}</p>
            ${oilPressureGraph.renderOilEnvelopeSingle([row], { singleFlight: true, idSuffix })}
          </section>
        `;
      })
      .filter(Boolean);
    if (!sections.length) {
      // No engine carried its own oil channels — e.g. a twin that logs a single
      // shared oil gauge on the unsuffixed oilTemp/oilPressure. Fall back to the
      // blended envelope (pre-#233 behavior) so the panel still renders instead
      // of regressing to an empty state.
      return mergedOilPanelBody(points, engineDescriptors, summary, flight);
    }
    // Name the engines using their own label scheme (Left/Right, or Front/Rear
    // for centerline twins) rather than hardcoding "left and right".
    const engineWords = engineDescriptors
      .map((engine) => String(engine.label ?? engine.shortLabel ?? "").replace(/\s*Engine$/i, "").trim().toLowerCase())
      .filter(Boolean);
    const compareClause = engineWords.length === engineDescriptors.length && engineWords.length
      ? `the ${engineWords.join(" and ")} engines`
      : "each engine";
    return subsection(
      "Oil pressure vs temperature — per engine",
      `Each engine's warm-pressure band on its own, so you can compare ${compareClause} directly instead of reading one averaged line.`,
      `<div class="overview-oil-engine-stack">${sections.join("")}</div>`,
    );
  }

  // EGT Rhythm tile (analysis key `valve`): the real per-cylinder EGT movement
  // each cylinder traces during steady power, with cylinder drill-down. Engine
  // scope shows the worst cylinder (the finding); Cyl N shows just that one.
  // egtRhythmWaveforms recomputes display series from points, with scalars read
  // from summary.valve.details so the drawn picture matches the tile's numbers.
  function egtRhythmPanelHtml(summary) {
    const points = getFlightData?.();
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const scopedPoints = activeFlightData(points, engineDescriptors);
    const scope = currentEngineScope();
    // Recompute only for left/right engine scope (same guard as the sensors
    // panel): the persisted summary ran with the full detector options
    // (engineDescriptors, phase windows, aircraft profile), so reusing it on
    // aircraft scope keeps the panel matching the tile's numbers and avoids a
    // synchronous detector pass on the main thread.
    const details = (scope === "left" || scope === "right") && Array.isArray(scopedPoints) && scopedPoints.length
      ? analyzeExhaustValvePattern(scopedPoints, {
        labelForKey: (key) => labelForKey(key, engineDescriptors),
      }).details
      : summary?.valve?.details;
    if (!Array.isArray(points) || !points.length || !details?.top?.best) {
      return wrapPanel(emptyStateHtml(summary?.valve?.note
        || "Analyze a flight with a steady-power EGT segment to see its rhythm signature."));
    }
    const flight = getSelectedFlight?.();
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const signature = `valve|${flight?.id ?? ""}|${scope}|${scopeSignature(engineDescriptors)}|${cylinderFilter}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const model = egtRhythmWaveforms(scopedPoints, details, {
      labelForKey: (key) => labelForKey(key, engineDescriptors),
    });
    let body;
    if (!model.available) {
      body = emptyStateHtml("No steady-power EGT segment with enough data to chart the rhythm in this flight.");
    } else {
      const focusLabel = cylinderFilter === "all" ? null : `Cyl ${cylinderFilter}`;
      body = subsection(
        "EGT rhythm signature",
        focusLabel
          ? `Where ${focusLabel}'s EGT rises and falls during steady power, against its own steady baseline.`
          : "The cylinder with the largest repeated EGT wave during steady power — a possible exhaust-valve cue.",
        `${egtRhythmSignature.renderDetail(model, { cylinderFilter })}${egtRhythmSignature.renderWaveform(model, { cylinderFilter })}`,
      );
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // EGT Balance tile (analysis key `egt`): how evenly the cylinders run. Two
  // flight-internal views recomputed from points — the per-cylinder EGT
  // distribution lanes (balance at a glance) and the cross-cylinder spread over
  // the flight against the score's threshold bands. Lanes honor cylinder focus;
  // the spread timeline is inherently engine-wide (it IS the cross-cylinder gap).
  function egtPanelHtml() {
    const points = getFlightData?.();
    if (!Array.isArray(points) || !points.length) {
      return wrapPanel(emptyStateHtml("Analyze a flight with at least two EGT channels to see its balance."));
    }
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const scopedDescriptors = activeEngineDescriptors(engineDescriptors);
    const sourceNumbers = activeSourceNumbers(engineDescriptors);
    const flight = getSelectedFlight?.();
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const signature = `egt|${flight?.id ?? ""}|${currentEngineScope()}|${scopeSignature(engineDescriptors)}|${cylinderFilter}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const byCylinder = egtDistributionSummary(points, { engineDescriptors })
      .filter((item) => matchesActiveEngine(item, sourceNumbers));
    const spread = egtSpreadTimelineSummary(points, { engineDescriptors: scopedDescriptors });
    const focusLabel = cylinderFilter === "all" ? null : `Cyl ${cylinderFilter}`;
    const body =
      subsection(
        focusLabel ? `${focusLabel} EGT distribution` : "Per-cylinder EGT distribution",
        focusLabel
          ? `Where ${focusLabel}'s EGT spent its loaded time this flight.`
          : "Where each cylinder's EGT spent its loaded time — tightly-stacked lanes mean the cylinders are burning evenly; a lane shifted hot or cool is the imbalance.",
        egtDistributionLanes.renderEgtDistributionLanes(byCylinder, { cylinderFilter }),
      ) +
      subsection(
        "Cylinder spread over the flight",
        "Hottest-minus-coolest EGT across all cylinders through the flight, against the balanced / compare / review bands. Shaded = under load, which is what the score weighs.",
        egtSpreadTimeline.renderEgtSpreadTimeline(spread),
      );
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // CHT Cooling Rate tile (analysis key `coolingRate`): rapid CHT cooling during
  // descents / power reductions. Engine scope shows per-cylinder metric tiles (from
  // the detector's own summaries, so the numbers match the score tile); selecting a
  // cylinder shows its whole-engine-run cooling-rate band graph against the 40/50
  // F/min compare/review bands, worst event window shaded. The trace comes from
  // coolingRateTraceForKey — the same windowed math the score uses, so line and
  // tile agree.
  function coolingPanelHtml(summary) {
    const flight = getSelectedFlight?.();
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const sourceNumbers = activeSourceNumbers(engineDescriptors);
    const thresholdFpm = Number.isFinite(Number(summary?.coolingRate?.details?.thresholdFpm))
      ? Number(summary.coolingRate.details.thresholdFpm)
      : Number(getCoolingRateThresholdFpm?.(flight?.aircraft) ?? 50);
    const signature = `coolingRate|${flight?.id ?? ""}|${currentEngineScope()}|${scopeSignature(engineDescriptors)}|${cylinderFilter}|threshold:${thresholdFpm}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const points = getFlightData?.();
    const scopedPoints = activeFlightData(points, engineDescriptors);
    // Prefer the detector's persisted per-cylinder summaries (matches the score
    // tile); if the display summary trimmed them, recompute from points so the
    // panel still renders. analyzeChtCoolingRate is one detector, memoized here.
    const persisted = summary?.coolingRate?.details?.summaries;
    const summaries = Array.isArray(persisted) && persisted.length
      ? persisted
      : (Array.isArray(scopedPoints) && scopedPoints.length ? (analyzeChtCoolingRate(scopedPoints, { thresholdFpm }).details?.summaries ?? []) : []);
    const scopedSummaries = summaries
      .filter((item) => matchesActiveEngine(item, sourceNumbers))
      .map((item) => relabelCylinderSummary(item, engineDescriptors));

    let body;
    if (cylinderFilter === "all") {
      if (!scopedSummaries.length) {
        body = emptyStateHtml("Analyze a flight with CHT channels and elapsed time to see per-cylinder cooling rates.");
      } else {
        body = subsection(
          "Per-cylinder cooling rate",
          `The fastest CHT cooling each cylinder saw this flight, colored by the review (${Math.round(thresholdFpm)} F/min) and compare bands.`,
          coolingRateGraph.renderCoolingTiles(scopedSummaries, { thresholdFpm }),
        );
      }
    } else {
      const cylSummary = scopedSummaries.find((item) => sourceNumberForItem(item) === Number(cylinderFilter));
      if (!cylSummary?.key || !Array.isArray(points) || !points.length) {
        body = emptyStateHtml(`No cooling-rate data for Cyl ${cylinderFilter} in this flight.`);
      } else {
        const trace = coolingRateTraceForKey(points, cylSummary.key);
        body = subsection(
          `${cylSummary.label ?? `Cyl ${cylinderFilter}`} cooling rate over the flight`,
          "Cooling rate (F/min) across the whole engine run — into the amber/red bands means rapid cooling; the shaded span is the worst event the score flagged.",
          coolingRateGraph.renderCoolingBand(trace, {
            label: cylSummary.label ?? `Cyl ${cylinderFilter}`,
            maxRateFpm: cylSummary.maxRateFpm,
            event: cylSummary.top ?? null,
            thresholdFpm,
          }),
        );
      }
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // CHT Cooling tile (analysis key `cooling`): cylinder cooling MARGIN / evenness —
  // which cylinder runs hottest (high-end CHT) against the warm (400) / review (420)
  // lines, and how much hotter than peers. Engine scope shows per-cylinder margin tiles
  // (from the detector's own summaries, matching the score); a selected cylinder shows
  // its CHT over the engine run against the bands, with an OAT/airspeed cooling-context
  // strip + high-cooling-demand shading when those channels are logged.
  function coolingMarginPanelHtml(summary) {
    const flight = getSelectedFlight?.();
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const sourceNumbers = activeSourceNumbers(engineDescriptors);
    const signature = `cooling|${flight?.id ?? ""}|${currentEngineScope()}|${scopeSignature(engineDescriptors)}|${cylinderFilter}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const points = getFlightData?.();
    const scopedPoints = activeFlightData(points, engineDescriptors);
    const persisted = summary?.cooling?.details?.summaries;
    const summaries = Array.isArray(persisted) && persisted.length
      ? persisted
      : (Array.isArray(scopedPoints) && scopedPoints.length ? (analyzeCoolingEfficiency(scopedPoints).details?.summaries ?? []) : []);
    const scopedSummaries = summaries
      .filter((item) => matchesActiveEngine(item, sourceNumbers))
      .map((item) => relabelCylinderSummary(item, engineDescriptors));

    let body;
    if (cylinderFilter === "all") {
      if (!scopedSummaries.length) {
        body = emptyStateHtml("Analyze a flight with multiple CHT channels under load to compare per-cylinder cooling.");
      } else {
        // OAT range over the engine-work segment (matches the "warm-segment" caption
        // and the cylinder view) — reuse the trace's gated OAT extent off any cylinder,
        // which also avoids a Math.min(...spread) RangeError on long flights.
        const oatRange = coolingMarginTraceForKey(points, scopedSummaries[0].key)?.oat ?? null;
        body = subsection(
          "High CHT By Cylinder",
          "Big numbers show high CHT. Small notes compare cylinders at the same moments.",
          coolingMarginGraph.renderMarginTiles(scopedSummaries, { oatRange }),
        );
      }
    } else {
      const cylSummary = scopedSummaries.find((item) => sourceNumberForItem(item) === Number(cylinderFilter));
      if (!cylSummary?.key || !Array.isArray(points) || !points.length) {
        body = emptyStateHtml(`No cooling-margin data for Cyl ${cylinderFilter} in this flight.`);
      } else {
        const trace = coolingMarginTraceForKey(points, cylSummary.key);
        body = subsection(
          `${cylSummary.label ?? `Cyl ${cylinderFilter}`} CHT cooling margin over the flight`,
          "CHT across the whole engine run against comfortable / warm (400) / review (420) bands. Shaded span = the worst evidence window; amber columns = high cooling demand.",
          coolingMarginGraph.renderMarginBand(trace, {
            label: cylSummary.label ?? `Cyl ${cylinderFilter}`,
            event: cylSummary.evidenceWindow ?? null,
          }),
        );
      }
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // CHT/EGT Pairing tile (analysis key `combustion`): whether each cylinder's CHT
  // tracks its EGT. Engine scope shows per-cylinder pairing tiles (paired after an
  // evaluated clear result, unavailable when no eligible window exists, and split
  // for the detector's flagged cylinders, over a points-derived roster); a
  // selected cylinder shows its CHT (left axis) + EGT (right axis) over the flight
  // against the faint peer average, with the split evidence window shaded.
  function combustionPanelHtml(summary) {
    const flight = getSelectedFlight?.();
    const focused = getSelectedCylinderNumber?.();
    const cylinderFilter = Number.isFinite(focused) ? focused : "all";
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const sourceNumbers = activeSourceNumbers(engineDescriptors);
    const signature = `combustion|${flight?.id ?? ""}|${currentEngineScope()}|${scopeSignature(engineDescriptors)}|${cylinderFilter}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const points = getFlightData?.();
    const scopedPoints = activeFlightData(points, engineDescriptors);
    const persistedMetric = summary?.combustion;
    const fallbackMetric = !persistedMetric && Array.isArray(scopedPoints) && scopedPoints.length
      ? analyzeCombustionSignatures(scopedPoints, { engineDescriptors })
      : null;
    const pairingMetric = persistedMetric ?? fallbackMetric;
    const persisted = pairingMetric?.details;
    const rawCandidates = Array.isArray(persisted?.candidates) ? persisted.candidates : [];
    const candidates = rawCandidates.filter((item) => !Number.isFinite(item?.cylinder) || matchesActiveEngine(item, sourceNumbers));
    const pairingEvaluation = chtEgtPairingEvaluationState(pairingMetric);

    let body;
    if (cylinderFilter === "all") {
      const roster = Array.isArray(points) && points.length
        ? chtEgtPairingRoster(points, { engineDescriptors })
          .filter((item) => matchesActiveEngine(item, sourceNumbers))
          .map((item) => ({ ...item, evaluated: chtEgtPairingEvaluationState(pairingMetric, item.engineKey).evaluated }))
        : [];
      const globalCues = candidates.filter((cand) => !Number.isFinite(cand?.cylinder)).map((cand) => cand.label).filter(Boolean);
      body = subsection(
        "Per-cylinder CHT/EGT pairing",
        "Whether each cylinder's CHT tracks its EGT (paired), one channel moves without the other (split), or the flight lacked an eligible settled window. Open a cylinder to see the traces against the peer average.",
        chtEgtPairingGraph.renderPairingTiles(roster, {
          candidates,
          globalCues,
          evaluated: pairingEvaluation.evaluated,
          unavailableReason: pairingEvaluation.reason,
        }),
      );
    } else if (!Array.isArray(points) || !points.length) {
      body = emptyStateHtml(`No CHT/EGT data for Cyl ${cylinderFilter} in this flight.`);
    } else {
      const trace = chtEgtPairingTraceForKey(points, `cht${cylinderFilter}`, { engineDescriptors });
      const cand = candidates.find((item) => Number(item?.cylinder) === Number(cylinderFilter));
      const label = scopedCylinderLabel(cylinderFilter, engineDescriptors, `Cyl ${cylinderFilter}`);
      body = subsection(
        `${label} CHT vs EGT over the flight`,
        "The cylinder's CHT (left axis) and EGT (right axis) against the faint peer average — paired means CHT tracks EGT; the shaded span is the split evidence window, if any.",
        chtEgtPairingGraph.renderPairingOverlay(trace, {
          label,
          event: cand && Number.isFinite(cand.startTime) && Number.isFinite(cand.endTime) ? { startTime: cand.startTime, endTime: cand.endTime } : null,
        }),
      );
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // Electrical and Start charge tiles share one data path: annotations (limits,
  // out-of-range window, start / power-off exclusions, the chargingRecovery
  // sub-result) read from summary.electrical.details so the picture matches the
  // tiles; the volts/amps traces are recomputed from points via electricalBusTrace.
  // Older cached bundles can carry trimmed details — when the limits are missing,
  // re-run analyzeElectrical on the points (one cheap pass) so the panels still
  // draw. Both are engine-wide (one bus) — they ignore cylinder focus, like the
  // EGT spread timeline.
  function electricalDetailsFor(summary, points) {
    const persisted = summary?.electrical?.details;
    return Number.isFinite(persisted?.lowLimit) && Number.isFinite(persisted?.highLimit)
      ? persisted
      : (analyzeElectrical(points).details ?? {});
  }

  // Electrical tile (analysis key `electrical`): the running-bus story — stat
  // tiles + the whole-log voltage band with its amps strip. The start-of-flight
  // story lives on the Start charge tile (chargingPanelHtml).
  function electricalPanelHtml(summary) {
    const points = getFlightData?.();
    if (!Array.isArray(points) || !points.length) {
      return wrapPanel(emptyStateHtml("Analyze a flight with a bus-voltage channel to see its electrical picture."));
    }
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const flight = getSelectedFlight?.();
    const signature = `electrical|${flight?.id ?? ""}|${scopeSignature(engineDescriptors)}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const details = electricalDetailsFor(summary, points);
    let body;
    if (!Number.isFinite(details?.lowLimit)) {
      body = emptyStateHtml(summary?.electrical?.note || "No bus-voltage channel logged in this flight.");
    } else {
      const trace = electricalBusTrace(points);
      const envelope = `${details.lowLimit}–${details.highLimit} V`;
      body = subsection(
        "Bus voltage",
        trace.hasAmps
          ? `The bus across the whole log against the ${envelope} charging envelope — inside the green band means the alternator is carrying the load. The amps strip shares the time axis: sustained discharge while the bus is low points at the alternator.`
          : `The bus across the whole log against the ${envelope} charging envelope — inside the green band means the alternator is carrying the load.`,
        `${electricalBusGraph.renderBusTiles(details, { hasAmps: trace.hasAmps })}${electricalBusGraph.renderBusBand(trace, details)}`,
      );
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  // Start charge tile (analysis key `charging`): the start-of-flight story —
  // cranking sag / verdict / recovery / spike tiles + the start-recovery zoom.
  function chargingPanelHtml(summary) {
    const points = getFlightData?.();
    if (!Array.isArray(points) || !points.length) {
      return wrapPanel(emptyStateHtml("Analyze a flight with a bus-voltage channel to grade the start charge."));
    }
    const engineDescriptors = currentEngineDescriptors?.() ?? [];
    const flight = getSelectedFlight?.();
    const signature = `charging|${flight?.id ?? ""}|${scopeSignature(engineDescriptors)}`;
    if (panelHtmlCache.has(signature)) return cachedPanelHtml(signature);

    const details = electricalDetailsFor(summary, points);
    const charging = details?.chargingRecovery;
    let body;
    if (!Number.isFinite(details?.lowLimit)) {
      body = emptyStateHtml(summary?.charging?.note || "No bus-voltage channel logged in this flight.");
    } else if (!charging || !Number.isFinite(charging.startTime)) {
      body = emptyStateHtml(summary?.charging?.note || "No engine start detected in this log, so there is no start-charge event to grade.");
    } else {
      const zoomEnd = Number.isFinite(charging.endTime) ? Math.max(charging.endTime, charging.startTime + 60) : charging.startTime + 90;
      const zoom = electricalBusTrace(points, { startTime: charging.startTime - 30, endTime: zoomEnd, maxBuckets: 400 });
      body = subsection(
        "Start charging recovery",
        "The minutes around engine start — the bus dips while cranking, then should climb back above the charging line within seconds as the alternator picks up the battery recharge.",
        `${electricalBusGraph.renderStartChargeTiles(details)}${electricalBusGraph.renderStartRecovery(zoom, charging)}`,
      );
    }
    const html = wrapPanel(body);
    storePanelHtml(signature, html);
    return html;
  }

  function emptyStateHtml(message) {
    return `<div class="overview-detector-visual-empty"><p>${escapeHtml(message)}</p></div>`;
  }

  function wrapPanel(body) {
    return `<div class="overview-detector-visual-body">${body}</div>`;
  }

  function bindPairingCardInteractions(panel) {
    if (!panel || typeof onChtEgtPairingCardSelect !== "function") return;
    panel.querySelectorAll("[data-pairing-cylinder]").forEach((button) => {
      button.addEventListener("click", () => {
        const cylinder = Number(button.dataset.pairingCylinder);
        if (!Number.isFinite(cylinder)) return;
        onChtEgtPairingCardSelect({
          cylinder,
          startTime: Number(button.dataset.pairingStart),
          endTime: Number(button.dataset.pairingEnd),
        });
      });
    });
  }

  // Called from the renderHealthSummary cycle (every overview render + tile tap).
  // `summary` is the engine-scoped displaySummary the rest of the overview reads;
  // the sensor panel reads its precomputed detail off it (the CHT panel ignores it
  // and recomputes from points).
  function renderOverviewDetectorVisual(summary) {
    const panel = document.querySelector("#overviewDetectorVisualPanel");
    if (!panel) return;
    const key = getSelectedHealthKey?.();
    if (!isOverviewActive() || !SUPPORTED_KEYS.has(key)) {
      panel.hidden = true;
      panel.innerHTML = "";
      document.body.classList.remove(DETECTOR_ACTIVE_CLASS);
      return;
    }
    panel.innerHTML = key === "sensors"
      ? sensorPanelHtml(summary)
      : key === "oilSystem"
        ? oilPanelHtml(summary)
        : key === "valve"
          ? egtRhythmPanelHtml(summary)
          : key === "egt"
            ? egtPanelHtml()
            : key === "coolingRate"
              ? coolingPanelHtml(summary)
              : key === "cooling"
                ? coolingMarginPanelHtml(summary)
                : key === "combustion"
                  ? combustionPanelHtml(summary)
                  : key === "electrical"
                    ? electricalPanelHtml(summary)
                    : key === "charging"
                      ? chargingPanelHtml(summary)
                      : chtPanelHtml();
    panel.hidden = false;
    if (key === "combustion") bindPairingCardInteractions(panel);
    // Hide the Operating Context cards while the detector panel is up, so the
    // graphs come into view (CSS keys off this class — see styles.css).
    document.body.classList.add(DETECTOR_ACTIVE_CLASS);
  }

  // Invalidate the memo when the selected flight's data changes underneath us.
  function resetOverviewDetectorVisual() {
    panelHtmlCache.clear();
  }

  return {
    renderOverviewDetectorVisual,
    resetOverviewDetectorVisual,
  };
}
